import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { euro } from "@/lib/i18n";
import { ArrowUpRight, Utensils, Table2, ClipboardList, TrendingUp, Sparkles } from "lucide-react";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  } catch { return null; }
}

export default function Overview({ restaurant }) {
  const [stats, setStats] = useState(null);
  const [counts, setCounts] = useState({ menu: 0, tables: 0, orders_active: 0 });

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setStats(r.data)).catch(() => {});
    Promise.all([
      api.get("/menu-items"),
      api.get("/tables"),
      api.get("/orders", { params: { status: "pending" } }),
    ])
      .then(([m, tb, o]) => setCounts({ menu: m.data.length, tables: tb.data.length, orders_active: o.data.length }))
      .catch(() => {});
  }, []);

  const status = restaurant?.subscription_status;
  const trialDays = status === "trialing" ? daysUntil(restaurant?.trial_ends_at) : null;
  const trialExpired = status === "trialing" && trialDays !== null && trialDays <= 0;

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Panoramica</div>
        <h1 className="font-display text-5xl font-black tracking-tighter mt-2">Ciao, {restaurant?.name || "…"} 👋</h1>
      </div>

      {trialExpired ? (
        <Link
          to="subscription"
          className="block p-5 rounded-2xl bg-red-50 border border-red-200 text-red-900 hover:bg-red-100 transition-colors"
          data-testid="overview-trial-expired"
        >
          <div className="flex justify-between items-center gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest">Attenzione</div>
              <div className="font-display text-xl font-bold mt-1">Prova terminata — attiva un piano per continuare</div>
            </div>
            <ArrowUpRight className="h-6 w-6" />
          </div>
        </Link>
      ) : status === "trialing" && trialDays !== null ? (
        <Link
          to="subscription"
          className="block p-5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 hover:bg-amber-100 transition-colors"
          data-testid="overview-trial-banner"
        >
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-widest">Periodo di prova</div>
                <div className="font-display text-xl font-bold mt-1">
                  {trialDays > 0 ? `${trialDays} giorno/i rimasti nella prova gratuita` : "La prova finisce oggi"}
                </div>
              </div>
            </div>
            <ArrowUpRight className="h-6 w-6" />
          </div>
        </Link>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Fatturato (pagato)", value: stats ? euro(stats.total_revenue, stats.currency) : "…", icon: TrendingUp, testid: "stat-revenue" },
          { label: "Ordini totali", value: stats?.total_orders ?? "…", icon: ClipboardList, testid: "stat-orders" },
          { label: "Piatti in menu", value: counts.menu, icon: Utensils, testid: "stat-menu" },
          { label: "Tavoli", value: counts.tables, icon: Table2, testid: "stat-tables" },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="p-6 bg-card border border-border rounded-2xl" data-testid={s.testid}>
              <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />
              <div className="mt-4 font-display text-3xl font-bold tracking-tight">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link to="menu" className="p-8 rounded-2xl bg-primary text-primary-foreground flex justify-between items-center hover:bg-[hsl(var(--accent))] transition-colors" data-testid="quick-menu">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-70">Crea il tuo menu</div>
            <div className="font-display text-2xl mt-2">Aggiungi categorie e piatti</div>
          </div>
          <ArrowUpRight className="h-6 w-6" />
        </Link>
        <Link to="tables" className="p-8 rounded-2xl bg-card border border-border flex justify-between items-center hover:border-[hsl(var(--accent))] transition-colors" data-testid="quick-tables">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Tavoli &amp; QR</div>
            <div className="font-display text-2xl mt-2">Stampa i QR code</div>
          </div>
          <ArrowUpRight className="h-6 w-6" />
        </Link>
      </div>
    </div>
  );
}
